import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import type { ReactElement } from 'react'
import type { HabitWithLog } from '../interfaces'
import { formatDeeplinkDisplay, openDeeplink } from '../utils/deeplink'
import {
  getHabitNameStyle,
  getHabitToggleStyle,
} from '../utils/habitCardStyles'
import { getHabitHighlight } from '../utils/habitHighlight'

interface SubHabitRowProps {
  subHabit: HabitWithLog
  parentId: string
  parentIsCompleted: boolean
  selectedDate: Date
  dayStartTime: string
  isReadOnlyDate?: boolean
  isFutureDate?: boolean
  onToggle: (subHabit: HabitWithLog, parentId: string) => Promise<void>
}

function getSubFrequencyLabel(habit: HabitWithLog): string {
  if (habit.frequency_type === 'daily') return 'Daily'
  if (habit.frequency_type === 'weekly') return 'Weekly'
  if (habit.frequency_type === 'weekly_days') {
    if (!habit.weekly_days || habit.weekly_days.length === 0)
      return 'Specific Days'
    const dayNames: Record<string, string> = {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    }
    return habit.weekly_days.map((d) => dayNames[d] ?? d).join(', ')
  }
  return `${habit.frequency_target ?? 1}×/wk`
}

export function SubHabitRow({
  subHabit,
  parentId,
  parentIsCompleted,
  selectedDate,
  dayStartTime,
  isReadOnlyDate,
  isFutureDate,
  onToggle,
}: SubHabitRowProps): ReactElement {
  const [toggling, setToggling] = useState(false)
  const isReadOnly = isReadOnlyDate ?? isFutureDate ?? false

  async function handleToggle(): Promise<void> {
    if (toggling || isReadOnly) return
    const isCompleting = !subHabit.isCompletedThisPeriod
    if (isCompleting && subHabit.deeplink_url) {
      openDeeplink(subHabit.deeplink_url)
    }
    setToggling(true)
    try {
      await onToggle(subHabit, parentId)
    } finally {
      setToggling(false)
    }
  }

  const isCompleted = subHabit.isCompletedThisPeriod
  const isSkipped = subHabit.isSkippedThisPeriod

  const toggleStyle = getHabitToggleStyle({
    isCompleted,
    isSkipped,
    isReadOnlyDate: isReadOnly,
  })

  const nameStyle = getHabitNameStyle(subHabit, isReadOnly, {
    isSubHabit: true,
  })

  const { borderClass, highlightReasonEmoji } = getHabitHighlight(
    subHabit,
    selectedDate,
    dayStartTime,
    { isSubHabit: true, parentIsCompleted },
  )

  return (
    <div
      className={`flex w-full items-center gap-2.5 rounded-xl border bg-gray-50 px-3 py-2 text-left transition-opacity ${
        toggling ? 'pointer-events-none opacity-50' : 'opacity-100'
      } hover:bg-gray-100 ${borderClass}`}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={toggling || isReadOnly}
        className="flex flex-1 items-center gap-2.5 text-left"
      >
        {/* Toggle circle */}
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${toggleStyle}`}
        >
          {toggling ? (
            <ArrowPathIcon className="h-3 w-3 animate-spin text-gray-400" />
          ) : isCompleted ? (
            <CheckIcon className="h-3 w-3" />
          ) : isSkipped ? (
            <MinusIcon className="h-3 w-3" />
          ) : null}
        </span>

        {/* Name */}
        <span
          className={`flex flex-1 items-center font-medium text-sm ${nameStyle}`}
        >
          {highlightReasonEmoji && (
            <span className="mr-1.5 inline-flex" title="Important today">
              {highlightReasonEmoji}
            </span>
          )}
          {subHabit.name}
        </span>
      </button>

      {/* Deeplink badge & launch action */}
      {subHabit.deeplink_url && (
        <button
          type="button"
          onClick={() => openDeeplink(subHabit.deeplink_url)}
          title={`Open ${subHabit.deeplink_url}`}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700 text-xs hover:bg-blue-100 active:bg-blue-200"
        >
          <span>📱</span>
          <span>{formatDeeplinkDisplay(subHabit.deeplink_url)}</span>
          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
        </button>
      )}

      {/* Frequency badge */}
      <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 font-medium text-gray-500 text-xs ring-1 ring-gray-200">
        {getSubFrequencyLabel(subHabit)}
      </span>

      {/* Weekly progress for x_per_week sub-habits */}
      {subHabit.frequency_type === 'x_per_week' && (
        <span
          className={`shrink-0 rounded-full px-1.5 py-0.5 font-medium text-xs ${
            subHabit.weeklyCompletions >= (subHabit.frequency_target ?? 1)
              ? 'bg-green-100 text-green-700'
              : 'bg-orange-100 text-orange-700'
          }`}
        >
          {subHabit.weeklyCompletions}/{subHabit.frequency_target ?? 1}
        </span>
      )}

      {/* Streak */}
      {subHabit.currentStreak > 0 && (
        <span className="shrink-0 text-orange-400 text-xs">
          🔥{subHabit.currentStreak}
        </span>
      )}
    </div>
  )
}
